import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2, RadioTower, RefreshCw, Save, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { marketingApi } from '@/lib/api';
import SocialInterestPicker from './SocialInterestPicker';
import SocialSourceToggles from './SocialSourceToggles';

const emptyDraft = { enabled_sources: [], selected_keywords: [], custom_keywords: [] };

export const SocialListeningInsights = () => {
  const { t } = useTranslation();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(emptyDraft);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    marketingApi.socialListening({ days })
      .then((payload) => {
        setData(payload);
        setDraft({
          enabled_sources: payload.preferences.enabled_sources || [],
          selected_keywords: payload.preferences.selected_keywords || [],
          custom_keywords: payload.preferences.custom_keywords || [],
        });
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };
  useEffect(load, [days]);

  const toggleSource = (source, checked) => setDraft((current) => ({
    ...current,
    enabled_sources: checked
      ? [...new Set([...current.enabled_sources, source])]
      : current.enabled_sources.filter((item) => item !== source),
  }));
  const save = async () => {
    setSaving(true);
    try {
      await marketingApi.saveSocialPreferences({
        enabled_sources: draft.enabled_sources,
        selected_keywords: draft.selected_keywords,
        custom_keywords: draft.custom_keywords.map((item) => item.label),
      });
      toast.success(t('social.saveSuccess'));
      window.dispatchEvent(new CustomEvent('marketing:opportunities-updated'));
      load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || t('social.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="border-b border-border bg-[linear-gradient(135deg,hsl(var(--background)),hsl(var(--secondary)/0.34))] p-4" data-testid="social-listening-insights">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-[13px] font-semibold"><RadioTower className="h-4 w-4 text-teal-700" />{t('social.title')}</div>
          <p className="mt-1 max-w-3xl text-[13px] text-muted-foreground">{t('social.privacyNote')}</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={days} onChange={(event) => setDays(Number(event.target.value))} className="h-8 rounded-sm border border-border bg-background px-2 text-[13px]" data-testid="social-listening-date-range">
            <option value="7">7 days</option><option value="30">30 days</option><option value="90">90 days</option>
          </select>
          <Button size="sm" variant="outline" onClick={load} className="h-8 px-2" data-testid="social-listening-refresh-button">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {loading ? <div className="flex min-h-36 items-center justify-center text-[13px] text-muted-foreground" data-testid="social-listening-loading"><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t('social.loadingSignals')}</div>
        : !data ? <div className="mt-4 flex items-center gap-2 border border-dashed border-border p-4 text-[13px] text-muted-foreground" data-testid="social-listening-error"><AlertTriangle className="h-4 w-4" />{t('social.unavailable')}</div>
          : <div className="mt-4 space-y-4">
            <SocialSourceToggles sources={data.sources} enabledSources={draft.enabled_sources} onToggle={toggleSource} />
            <SocialInterestPicker
              catalog={[...data.catalog, ...draft.custom_keywords.filter((item) => !data.catalog.some((row) => row.slug === item.slug)).map((item) => ({ ...item, category: 'Custom', custom: true }))]}
              selected={draft.selected_keywords}
              customKeywords={draft.custom_keywords}
              onSelectedChange={(selected_keywords) => setDraft((current) => ({ ...current, selected_keywords }))}
              onCustomChange={(custom_keywords) => setDraft((current) => ({ ...current, custom_keywords }))}
            />
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
              <span className="text-[13px] text-muted-foreground" data-testid="social-listening-privacy-threshold">{t('social.privacyThreshold', { count: data.privacy_threshold })}</span>
              <Button size="sm" onClick={save} disabled={saving} className="h-8 gap-1.5" data-testid="social-listening-save-button">
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} {t('social.saveChoices')}
              </Button>
            </div>
            <TrendTable rows={data.trends} threshold={data.privacy_threshold} t={t} />
          </div>}
    </section>
  );
};

const TrendTable = ({ rows, threshold, t }) => (
  <div data-testid="social-listening-trends">
    <div className="mb-2 flex items-center gap-2 text-[13.5px] font-semibold"><Sparkles className="h-4 w-4 text-amber-600" />{t('social.momentum')}</div>
    {rows.length ? <div className="overflow-x-auto border-y border-border"><table className="w-full min-w-[560px] text-[13px]"><thead className="bg-secondary/50 text-left uppercase text-muted-foreground"><tr><th className="px-2 py-2">{t('social.colInterest')}</th><th className="px-2 py-2">{t('social.colStream')}</th><th className="px-2 py-2 text-right">{t('social.colMentions')}</th><th className="px-2 py-2 text-right">{t('social.colMomentum')}</th><th className="px-2 py-2">{t('social.colSignal')}</th></tr></thead><tbody>{rows.map((row) => <tr key={`${row.source}-${row.keyword_slug}`} className="border-t border-border" data-testid={`social-trend-${row.source}-${row.keyword_slug}`}><td className="px-2 py-2"><div className="font-semibold" data-testid={`social-trend-${row.source}-${row.keyword_slug}-keyword`}>{row.keyword}</div><div className="text-[9px] text-muted-foreground">{row.category}</div></td><td className="px-2 py-2 capitalize">{row.source}</td><td className="px-2 py-2 text-right font-mono" data-testid={`social-trend-${row.source}-${row.keyword_slug}-count`}>{row.count}</td><td className={`px-2 py-2 text-right font-mono ${row.delta_pct > 0 ? 'text-emerald-700' : row.delta_pct < 0 ? 'text-rose-700' : ''}`}>{row.delta_pct > 0 ? '+' : ''}{row.delta_pct}%</td><td className="px-2 py-2">{row.high_interest ? <span className="rounded-full bg-amber-100 px-2 py-1 text-[9px] font-bold uppercase text-amber-800" data-testid={`social-trend-${row.source}-${row.keyword_slug}-high-interest`}>{t('social.highInterest')}</span> : <span className="text-muted-foreground">{t('social.watching')}</span>}</td></tr>)}</tbody></table></div>
      : <div className="border border-dashed border-border p-4 text-[13px] text-muted-foreground" data-testid="social-listening-trends-empty">{t('social.emptyTrends', { count: threshold })}</div>}
  </div>
);

export default SocialListeningInsights;